import React, { useState, useRef } from "react";
import { Box } from "@mui/material";

const SwipeableCard = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  swipeThreshold = 100, // Minimum distance in pixels to trigger swipe
}) => {
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef(null);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setCurrentX(touch.clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    // Only allow horizontal swipe if horizontal movement is greater than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault(); // Prevent scrolling
      setCurrentX(touch.clientX);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;

    const deltaX = currentX - startX;
    const deltaY = Math.abs(e.changedTouches[0].clientY - startY);

    // Only trigger swipe if:
    // 1. Horizontal movement is greater than vertical (prevents accidental swipes during scroll)
    // 2. Movement exceeds threshold
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > swipeThreshold) {
      if (deltaX < 0 && onSwipeLeft) {
        // Swipe left (negative deltaX)
        onSwipeLeft();
      } else if (deltaX > 0 && onSwipeRight) {
        // Swipe right (positive deltaX)
        onSwipeRight();
      }
    }

    // Reset
    setIsDragging(false);
    setStartX(0);
    setStartY(0);
    setCurrentX(0);
  };

  // Reset on mouse leave (in case user drags and releases outside)
  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setStartX(0);
      setStartY(0);
      setCurrentX(0);
    }
  };

  // Calculate transform for visual feedback
  const deltaX = currentX - startX;
  const transformX = isDragging && Math.abs(deltaX) > 10 ? deltaX : 0;
  const opacity = isDragging && Math.abs(deltaX) > swipeThreshold ? 0.7 : 1;

  // Visual feedback colors
  let backgroundColor = "transparent";
  if (isDragging && Math.abs(deltaX) > swipeThreshold) {
    if (deltaX < 0) {
      // Swipe left (approve) - green
      backgroundColor = "rgba(76, 175, 80, 0.1)";
    } else {
      // Swipe right (reject) - red
      backgroundColor = "rgba(244, 67, 54, 0.1)";
    }
  }

  return (
    <Box
      ref={cardRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: "relative",
        touchAction: "pan-y", // Allow vertical scrolling, prevent horizontal
        userSelect: "none",
        transition: isDragging ? "none" : "transform 0.3s ease, opacity 0.3s ease",
        transform: `translateX(${transformX}px)`,
        opacity: opacity,
        backgroundColor: backgroundColor,
        borderRadius: "12px",
      }}
    >
      {children}
      
      {/* Visual feedback overlay */}
      {isDragging && Math.abs(deltaX) > swipeThreshold && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: "12px",
          }}
        >
          {deltaX < 0 ? (
            <Box
              sx={{
                backgroundColor: "rgba(76, 175, 80, 0.9)",
                color: "#fff",
                px: 3,
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "16px",
              }}
            >
              ✓ Approve
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: "rgba(244, 67, 54, 0.9)",
                color: "#fff",
                px: 3,
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "16px",
              }}
            >
              ✗ Reject
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SwipeableCard;
